import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatCurrency, formatDate } from "@/lib/utils";
import { DollarSign, TrendingUp, Trophy, FileText, Send, Download } from "lucide-react";

function downloadCsv(data: { csv: string; filename: string }) {
  const blob = new Blob([data.csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = data.filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Revenue() {
  const { data: stats, isLoading } = trpc.admin.billing.stats.useQuery();
  const { data: serviceInvoices, isLoading: invLoading } = trpc.admin.billing.serviceInvoices.useQuery({ page: 1, limit: 10 });
  const { data: successFees, isLoading: feeLoading } = trpc.admin.billing.successFees.useQuery({ page: 1, limit: 10, status: "DUE" });

  const exportRevenue = trpc.admin.billing.exportRevenue.useMutation({
    onSuccess: (data: any) => downloadCsv(data),
  });

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">Revenue</h1>
            <p className="text-muted-foreground">Métriques financières globales de la plateforme</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => exportRevenue.mutate({} as any)}
            disabled={exportRevenue.isPending}
          >
            <Download className="h-4 w-4 mr-2" />
            {exportRevenue.isPending ? "Export…" : "Export CSV"}
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">CA Service (mois)</CardTitle>
              <DollarSign className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-24" /> : (
                <>
                  <div className="text-2xl font-bold text-success">{formatCurrency(stats?.serviceRevenue ?? 0)}</div>
                  <p className="text-xs text-muted-foreground">{stats?.invoicesPaidThisMonth ?? 0} factures payées</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Assistance payée (total)</CardTitle>
              <TrendingUp className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-24" /> : (
                <div className="text-2xl font-bold">{formatCurrency(stats?.assistancePaidTotal ?? 0)}</div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success fees payés (total)</CardTitle>
              <Trophy className="h-4 w-4 text-success" />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-24" /> : (
                <div className="text-2xl font-bold">{formatCurrency(stats?.successFeePaidTotal ?? 0)}</div>
              )}
            </CardContent>
          </Card>

          <Card className={(stats?.invoicesToIssue ?? 0) > 0 ? "border-warning/30" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Factures à émettre</CardTitle>
              <Send className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-12" /> : (
                <>
                  <div className="text-2xl font-bold text-warning">{stats?.invoicesToIssue ?? 0}</div>
                  <p className="text-xs text-muted-foreground">{stats?.invoicesPending ?? 0} en attente paiement</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Pending revenue */}
        <div className="grid gap-3 md:grid-cols-2">
          <Card className={(stats?.assistanceDueTotal ?? 0) > 0 ? "border-warning/30" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Assistance à encaisser</CardTitle>
              <FileText className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-24" /> : (
                <>
                  <div className="text-2xl font-bold text-warning">{formatCurrency(stats?.assistanceDueTotal ?? 0)}</div>
                  <p className="text-xs text-muted-foreground">{stats?.assistanceDueCount ?? 0} commandes DUE</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card className={(stats?.successFeeDueTotal ?? 0) > 0 ? "border-warning/30" : ""}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success fees à encaisser</CardTitle>
              <Trophy className="h-4 w-4 text-warning" />
            </CardHeader>
            <CardContent>
              {isLoading ? <Skeleton className="h-8 w-24" /> : (
                <>
                  <div className="text-2xl font-bold text-warning">{formatCurrency(stats?.successFeeDueTotal ?? 0)}</div>
                  <p className="text-xs text-muted-foreground">{stats?.successFeeDueCount ?? 0} factures DUE</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent invoices */}
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dernières factures service</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium text-muted-foreground">Entreprise</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Montant</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-3"><Skeleton className="h-4 w-32" /></td>
                          <td className="p-3"><Skeleton className="h-4 w-20" /></td>
                          <td className="p-3"><Skeleton className="h-5 w-14" /></td>
                        </tr>
                      ))
                    ) : (serviceInvoices?.items ?? []).length === 0 ? (
                      <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Aucune facture</td></tr>
                    ) : (
                      (serviceInvoices?.items ?? []).map((inv: any) => (
                        <tr key={inv.id} className="border-b hover:bg-muted/30">
                          <td className="p-3 font-medium">{inv.Company?.name ?? "—"}</td>
                          <td className="p-3">{formatCurrency(inv.amount ?? 0)}</td>
                          <td className="p-3">
                            <Badge variant={inv.status === "PAID" ? "success" : inv.status === "SENT" ? "default" : "secondary"}>
                              {inv.status}
                            </Badge>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Success fees en attente</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium text-muted-foreground">Entreprise</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Montant</th>
                      <th className="text-left p-3 font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-3"><Skeleton className="h-4 w-32" /></td>
                          <td className="p-3"><Skeleton className="h-4 w-20" /></td>
                          <td className="p-3"><Skeleton className="h-4 w-20" /></td>
                        </tr>
                      ))
                    ) : (successFees?.items ?? []).length === 0 ? (
                      <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Aucun success fee en attente</td></tr>
                    ) : (
                      (successFees?.items ?? []).map((fee: any) => (
                        <tr key={fee.id} className="border-b hover:bg-muted/30">
                          <td className="p-3 font-medium">{fee.Company?.name ?? "—"}</td>
                          <td className="p-3 text-warning font-medium">{formatCurrency(fee.feeAmount ?? 0)}</td>
                          <td className="p-3 text-muted-foreground">{formatDate(fee.issuedAt)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
